<template>
  <DarkFull>
    <div class="min-h-full flex flex-col bg-gray-950">
      <div
        class="w-full h-16 border-b border-gray-900 flex items-center justify-center"
      >
        <img class="mx-auto h-6 w-auto" :src="Logo" :alt="$t('account.logo_alt')" />
      </div>

      <div
        class="sm:mx-auto sm:w-full sm:max-w-md flex items-center justify-center flex-1"
      >
        <div
          class="sm:mx-auto sm:w-full sm:max-w-md bg-gray-900 rounded-lg py-8 px-6 shadow sm:rounded-md"
        >
          <div class="space-y-6 border-b border-gray-800 pb-6">
            <div
              class="flex flex-col w-full items-center border-b border-gray-800 pb-6"
            >
              <h1 class="text-text-primary font-semibold text-[24px]">
                {{ $t('account.register.title') }}
              </h1>
              <h2 class="text-text-secondary">
                <i18n-t scope="global" v-if="username" keypath="account.register.greeting" tag="span">
                  <template #username><span class="text-text-primary">{{ username }}</span></template>
                  <template #steam_icon><img class="inline w-4 h-4 brightness-200" src="../assets/icons/steam.svg" /></template>
                </i18n-t>
                <span v-else>
                  {{ $t('account.register.create_account') }}
                </span>
                <span v-if="esid" class="block text-center">
                  {{ $t('account.register.steam_account_linked') }}
                </span>
                <span v-if="join" class="block text-center text-primary-300">
                  {{ $t('play.lan_party.login_context', { name: join }) }}
                </span>
              </h2>
            </div>
            <div v-if="!esid">
              <form
                action="https://steamcommunity.com/openid/login"
                method="get"
                @submit="savePendingRegister"
              >
                <input
                  type="hidden"
                  name="openid.identity"
                  value="http://specs.openid.net/auth/2.0/identifier_select"
                />
                <input
                  type="hidden"
                  name="openid.claimed_id"
                  value="http://specs.openid.net/auth/2.0/identifier_select"
                />
                <input
                  type="hidden"
                  name="openid.ns"
                  value="http://specs.openid.net/auth/2.0"
                />
                <input
                  type="hidden"
                  name="openid.mode"
                  value="checkid_setup"
                />
                <input
                  type="hidden"
                  name="openid.return_to"
                  :value="steamAuthURL"
                />
                <input
                  type="hidden"
                  name="openid.realm"
                  :value="steamAuthURL"
                />
                <button
                  type="submit"
                  class="justify-center items-center w-full py-3 px-4 flex gap-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-text-primary bg-primary-600 hover:bg-primary-500 duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <img class="w-4 h-4" src="../assets/icons/steam-icon.svg" />
                  {{ $t('account.register.with_steam') }}
                </button>
              </form>
              <p class="text-text-secondary text-xs text-center pt-2">
                {{ $t('account.register.steam_no_password') }}
              </p>
              <div class="relative flex pt-6 items-center">
                <div class="flex-grow border-t border-gray-800" />
                <span
                  class="flex-shrink mx-4 text-text-secondary uppercase font-light text-sm tracking-[1.5px]"
                >{{ $t('account.register.or_with_email') }}</span
                >
                <div class="flex-grow border-t border-gray-800" />
              </div>
            </div>
            <div>
              <label
                for="email"
                class="block text-sm font-medium text-gray-100"
              >
                {{ $t('account.fields.email') }}
              </label>
              <div class="mt-2">
                <input
                  ref="emailInput"
                  autofocus
                  v-model="state.form.body.email"
                  id="email"
                  name="email"
                  type="email"
                  autocomplete="email"
                  :placeholder="$t('account.fields.email_placeholder')"
                  required="true"
                  class="text-text-primary placeholder:text-text-secondary bg-gray-800 appearance-none block w-full px-3 py-2 border border-gray-700 rounded-md shadow-sm placeholder-gray-200 focus:outline-none focus:ring-gray-400 focus:border-gray-400 sm:text-sm"
                />
              </div>
              <div class="text-red-500 text-sm">
                {{ state.form.errors.email }}
              </div>
            </div>

            <div>
              <label
                for="password"
                class="block text-sm font-medium text-gray-100"
              >
                {{ $t('account.fields.password') }}
              </label>
              <div class="mt-2">
                <input
                  v-model="state.form.body.password"
                  id="password"
                  name="password"
                  type="password"
                  autocomplete="current-password"
                  :placeholder="$t('account.register.password_placeholder')"
                  required="true"
                  class="text-text-primary placeholder:text-text-secondary bg-gray-800 appearance-none block w-full px-3 py-2 border border-gray-700 rounded-md shadow-sm placeholder-gray-200 focus:outline-none focus:ring-gray-400 focus:border-gray-400 sm:text-sm"
                />
              </div>
              <div class="text-red-500 text-sm">
                {{ state.form.errors.password }}
              </div>
            </div>

            <div>
              <label
                for="password_confirmation"
                class="block text-sm font-medium text-gray-100"
              >
                {{ $t('account.fields.password_confirmation') }}
              </label>
              <div class="mt-2">
                <input
                  v-model="state.form.body.password_confirmation"
                  id="password_confirmation"
                  name="password_confirmation"
                  type="password"
                  autocomplete="password_confirmation"
                  :placeholder="$t('account.register.password_confirmation_placeholder')"
                  required="true"
                  class="text-text-primary placeholder:text-text-secondary bg-gray-800 appearance-none block w-full px-3 py-2 border border-gray-700 rounded-md shadow-sm placeholder-gray-200 focus:outline-none focus:ring-gray-400 focus:border-gray-400 sm:text-sm"
                />
              </div>
              <div class="text-red-500 text-sm">
                {{ state.form.errors.password_confirmation }}
              </div>
            </div>
            <div class="relative flex flex-col items-start">
              <div class="flex items-center h-5">
                <input
                  id="candidates"
                  aria-describedby="candidates-description"
                  type="checkbox"
                  class="outline-none focus:outline-none h-4 w-4 text-primary-500 border-gray-400 rounded bg-transparent"
                  v-model="state.form.body.email_sign_up"
                />
                <div class="ml-3 text-sm">
                  <label
                    for="candidates"
                    class="font-medium text-text-primary"
                  >{{ $t('account.register.receive_emails') }}</label
                  >
                </div>
              </div>
              <p
                id="candidates-description"
                class="text-text-secondary text-xs pt-2"
              >
                {{ $t('account.register.receive_emails_hint') }}
              </p>
            </div>
            <div>
              <button
                @click="registerRedirect"
                type="submit"
                class="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-text-primary bg-gray-800 hover:bg-gray-700 duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                :disabled="state.form.loading"
              >
                <div v-if="state.form.loading" class="flex items-center gap-2">
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 32 32"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    class="animate-spin"
                  >
                    <circle
                      cx="16"
                      cy="16"
                      r="15"
                      stroke="#ffffff"
                      stroke-width="2"
                    />
                    <path
                      d="M16 1C17.9698 1 19.9204 1.38799 21.7403 2.14181C23.5601 2.89563 25.2137 4.00052 26.6066 5.3934C27.9995 6.78628 29.1044 8.43986 29.8582 10.2597C30.612 12.0796 31 14.0302 31 16"
                      stroke="#4E54C8"
                      stroke-width="2"
                    />
                  </svg>
                  <span>{{ $t('common.loading') }}</span>
                </div>
                <span v-else>{{ $t('account.register.title') }}</span>
              </button>
            </div>
            <div>
              <i18n-t scope="global" keypath="account.register.accept_terms" tag="p" class="text-text-secondary text-xs text-center">
                <template #privacy_policy>
                  <!-- eslint-disable-next-line vuejs-accessibility/anchor-has-content -- content is the i18n-t child -->
                  <a
                    target="_blank"
                    class="text-primary-400"
                    href="https://wiki.refrag.gg/privacy-policy"
                  ><i18n-t scope="global" keypath="account.register.privacy_policy_wrapped"><template #br><br /></template></i18n-t></a
                  >
                </template>
                <template #terms_of_service>
                  <a
                    target="_blank"
                    class="text-primary-400"
                    href="https://wiki.refrag.gg/terms-of-service"
                  >{{ $t('account.terms_of_service') }}</a
                  >
                </template>
              </i18n-t>
            </div>
          </div>
          <div class="flex gap-2 w-full items-center justify-center pt-6">
            <h1 class="text-text-primary text-sm">{{ $t('account.register.has_account') }}</h1>
            <a
              @click="navToLogin"
              class="text-primary-400 underline text-xs cursor-pointer hover:text-primary-300"
            >{{ $t('account.register.log_in_here') }}</a
            >
          </div>
        </div>
      </div>
    </div>
  </DarkFull>
</template>

<script>

import { useAuth } from '@websanova/vue-auth/src/v3.js';
import { ref, onMounted, reactive } from 'vue';
import { useHead } from '@unhead/vue';
import { useRouter, useRoute } from 'vue-router';
import { useGtm } from '@gtm-support/vue-gtm';
import { useI18n } from 'vue-i18n';
import DarkFull from '../components/layouts/DarkFull.vue';
import Logo from '../utils/logo';
import { signInStarted, trackEvent } from '../utils/analytics';
import { getSteamAuthURL } from '../utils/steam_openid';
import SpinnerIcon from '../assets/loading.svg';

// The query and hash a player arrived with, kept across the Steam round trip:
// login_with_steam sends a new Steam id back to /register?esid=… and drops them.
// session.js keeps this key on the signed-out boot that return trip causes.
const PENDING_REGISTER_KEY = 'refrag_register_pending';

export default {
  setup() {
    const auth = useAuth();
    const router = useRouter();
    const route = useRoute();
    const gtm = useGtm();
    const { t } = useI18n();
    const username = ref(route.query.username);
    const { esid, join } = route.query;
    const steamAuthURL = getSteamAuthURL(join ? `/join/${join}` : '/', { join });
    const emailInput = ref(null);

    const state = reactive({
      form: {
        body: {
          email: '',
          password: '',
          password_confirmation: '',
          email_sign_up: false,
          confirm_success_url: 'https://play.refrag.gg/',
        },
        remember: false,
        fetchUser: true,
        staySignedIn: true,
        errors: {},
        loading: false,
      },
    });

    function extractClaimTrialSource(targetRoute) {
      const sourceParam = targetRoute?.params?.source;
      if (typeof sourceParam === 'string' && sourceParam.trim()) {
        return sourceParam.trim();
      }

      const path = targetRoute?.path || targetRoute?.fullPath || '';
      const match = path.match(/^\/claim_trial\/([^/?#]+)/);

      if (!match?.[1]) return null;

      return decodeURIComponent(match[1]);
    }

    function persistTrialCookie(source) {
      if (!source) return;

      const cookie = `trial=${encodeURIComponent(source)}; path=/; max-age=${60 * 60 * 24 * 30}; SameSite=Lax`;
      document.cookie = cookie;

      if (window.location.hostname.endsWith('refrag.gg')) {
        document.cookie = `${cookie}; domain=.refrag.gg`;
      }
    }

    function savePendingRegister() {
      const { esid: _esid, username: _username, ...query } = route.query;
      try {
        sessionStorage.setItem(
          PENDING_REGISTER_KEY,
          JSON.stringify({ query, hash: route.hash }),
        );
      } catch (e) {
        // Storage can be unavailable (private mode); Steam sign-up still works without it.
      }
    }

    function restorePendingRegister() {
      let pending = null;
      try {
        pending = JSON.parse(sessionStorage.getItem(PENDING_REGISTER_KEY));
        sessionStorage.removeItem(PENDING_REGISTER_KEY);
      } catch (e) {
        return;
      }
      if (!pending) return;

      router.replace({
        query: { ...pending.query, ...route.query },
        hash: route.hash || pending.hash || '',
      });
    }

    onMounted(() => {
      const trialSource = extractClaimTrialSource(auth.redirect()?.from);
      persistTrialCookie(trialSource);

      if (esid) {
        restorePendingRegister();
        emailInput.value?.focus();
      }
    });

    useHead({
      title: t('account.register.page_title'),
      htmlAttrs: {
        lang: 'en',
        amp: true,
      },
    });

    function errors(res) {
      const err = res.data?.errors ?? {};

      state.form.errors.email = err.email?.[0] ?? '';
      state.form.errors.password = err.password?.[0] ?? '';
      state.form.errors.password_confirmation = err.password_confirmation?.[0] ?? '';
    }

    function registerRedirect() {
      state.form.loading = true;
      signInStarted();
      auth
        .register({
          data: {
            ...state.form.body,
            esid, // Encrypted Steam Id
            join, // The creator whose LAN party link brought them here
          },
          fetchUser: state.form.fetchUser,
          redirect: join ? { name: 'JoinPage', params: { slug: join } } : { name: 'Dashboard' },
          staySignedIn: true,
          autoLogin: true,
          fetchUser: true,
        })
        .then((res) => {
          const method = route.query.esid ? 'steam' : 'email';
          gtm.push({
            event: 'register',
            email: state.form.body.email,
            user_id: res.data.data.id,
            method,
          });
          // Identified by the /me hook in main.js.
          trackEvent('register', { method });
          // If we have a 'code' query parameter, redirect to redeem page
          if (route.query.code) {
            router.push({
              name: 'Redeem',
              query: route.query,
            });
          } else if (!join) {
            router.push(`/upgrade${route.hash}` || '');
          }
        })
        .then(null, (res) => {
          errors(res.response);
        })
        .finally(() => {
          state.form.loading = false;
        });
    }

    function navToLogin() {
      router.push({
        name: 'Login',
        query: route.query,
      });
    }

    return {
      state,
      registerRedirect,
      navToLogin,
      SpinnerIcon,
      username,
      esid,
      join,
      steamAuthURL,
      emailInput,
      savePendingRegister,
    };
  },
  data() {
    return {
      Logo,
    };
  },
  components: {
    DarkFull,
  },
};
</script>
